import React, { useEffect, useRef, useState } from 'react';
import { BellRing, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.35, 0.7].forEach((t) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.25, ctx.currentTime + t); g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.3);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.3);
    });
  } catch { /* audio blocked */ }
};

const alarmAt = (d) => new Date(d.status === 'snoozed' && d.snooze_until ? d.snooze_until : d.due_at).getTime();

// In-page dose alarm — only while the recovery page is open (no server clock).
export default function DoseAlarm({ planId, doses, onChanged }) {
  const [ring, setRing] = useState(null);
  const shown = useRef(new Set());

  useEffect(() => {
    const now = Date.now();
    const lateOk = (d) => new Date(d.late_until).getTime() > now;
    const active = doses.filter((d) => ['pending', 'snoozed'].includes(d.status) && lateOk(d));
    const due = active.find((d) => alarmAt(d) <= now && !shown.current.has(`${d.id}:${d.status}:${d.snooze_until}`));
    if (due) {
      shown.current.add(`${due.id}:${due.status}:${due.snooze_until}`);
      setRing(due); beep(); navigator.vibrate?.([300, 150, 300, 150, 300]);
      if (window.Notification?.permission === 'granted') {
        navigator.serviceWorker?.ready.then((reg) => reg.showNotification(`Time for ${due.med_name}`, {
          body: 'Tap Taken once you have had it.', tag: `dose-${due.id}`, requireInteraction: true, icon: '/logo192.png',
          data: { url: `/recovery/${planId}`, dose_token: due.token },
          actions: [{ action: 'taken', title: 'Taken' }, { action: 'snooze', title: 'Snooze 15 min' }],
        })).catch(() => {});
      }
      return undefined;
    }
    const next = active.map(alarmAt).filter((t) => t > now).sort((a, b) => a - b)[0];
    if (!next) return undefined;
    const t = setTimeout(() => onChanged(), Math.min(next - now + 500, 2 ** 31 - 1));
    return () => clearTimeout(t);
  }, [doses, planId, onChanged]);

  if (!ring) return null;
  const act = async (action) => {
    try { await api.post(`/patient/recovery/${planId}/dose/${ring.id}`, { action }); toast.success(action === 'taken' ? 'Marked as taken' : 'We will remind you in 15 minutes'); }
    catch { toast.error('Could not update this dose'); }
    setRing(null); onChanged();
  };
  return (
    <div className="fixed inset-x-0 top-0 z-50 p-3" data-testid="recovery-dose-alarm">
      <div className="max-w-xl mx-auto rounded-2xl bg-[#0b1f3a] text-white p-5 shadow-2xl border-2 border-teal-400">
        <div className="flex items-center gap-2 text-teal-300 text-[12px] font-bold uppercase tracking-[.18em]"><BellRing size={15} /> Medicine time</div>
        <div className="mt-1 text-2xl font-extrabold" data-testid="recovery-dose-alarm-name">{ring.med_name}</div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button type="button" onClick={() => act('taken')} className="py-4 rounded-xl bg-emerald-500 text-white font-extrabold text-[16px] flex items-center justify-center gap-2" data-testid="recovery-dose-alarm-taken"><Check size={18} /> Taken</button>
          <button type="button" onClick={() => act('snooze')} className="py-4 rounded-xl bg-white/10 border border-white/30 font-bold text-[15px]" data-testid="recovery-dose-alarm-snooze">Snooze 15 min</button>
        </div>
      </div>
    </div>
  );
}
